import React, { useEffect, useState, useCallback } from 'react';
import { useGame, api } from '../context/GameContext';
import { Plus, Layers, Coins, Pencil, Lock } from 'lucide-react';
import { toast } from 'sonner';
import ChessRook from './ChessRook';
import CollectionManageSheet from './CollectionManageSheet';
import { SetBadges } from './CollectionSetBadges';
import thumb from '../utils/thumb';

const fmt = (n) => (n || 0).toLocaleString('en-US');


// Player showcases. A painting in a collection keeps its normal slot and
// keeps earning — collections are a display layer + late-game coin sink.
const CollectionsPanel = () => {
  const { user, language, refreshUser, haptic, t } = useGame();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [openId, setOpenId] = useState(null);



  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/collections/me');
      setState(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createCost = state?.create_cost || 100000;
  const canAfford = (user?.coins || 0) >= createCost;

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name || submitting) return;
    haptic?.();
    setSubmitting(true);
    try {
      const { data } = await api.post('/collections', { name });
      setNewName('');
      setCreating(false);
      toast.success(
        t('coll_created').replace('{name}', name)
        + (data.cost ? ` (−${fmt(data.cost)})` : '')
      );
      await Promise.all([load(), refreshUser?.()]);
      setOpenId(data.collection?.id || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('coll_create_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (coll) => {
    haptic?.();
    try {
      await api.delete(`/collections/${coll.id}`);
      toast(t('coll_deleted').replace('{name}', coll.name));
      setOpenId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error');
    }
  };

  if (loading) {
    return (
      <div className="py-16 text-center text-[#A1A1AA] text-sm animate-pulse" data-testid="collections-loading">
        {t('coll_loading')}
      </div>
    );
  }

  const collections = state?.collections || [];
  const open = collections.find((c) => c.id === openId) || null;

  return (
    <div data-testid="collections-panel">
      {/* Create row */}
      {creating ? (
        <div className="glass-card rounded-xl p-3 mb-3 border border-[#D4AF37]/25">
          <input
            data-testid="collection-name-input"
            autoFocus
            value={newName}
            maxLength={40}
            onChange={(e) => setNewName(e.target.value)}
            placeholder={t('coll_name_placeholder')}
            className="w-full bg-[#0a0a12] border border-white/10 rounded-lg px-3 py-2 text-sm text-[#F5F5F7] placeholder:text-[#52525B] outline-none focus:border-[#D4AF37]/60 transition-colors"
          />
          <div className="flex items-center gap-2 mt-2.5">
            <button
              data-testid="collection-create-confirm"
              onClick={handleCreate}
              disabled={!newName.trim() || !canAfford || submitting}
              className="flex-1 py-2 rounded-lg bg-gradient-to-r from-[#D4AF37] to-[#F5D07A] text-[#0C0C14] text-sm font-bold disabled:opacity-40 transition-transform active:scale-[0.98]"
            >
              {submitting ? t('coll_creating') : `${t('coll_create')} · ${fmt(createCost)}`}
            </button>
            <button
              data-testid="collection-create-cancel"
              onClick={() => { setCreating(false); setNewName(''); }}
              className="px-3 py-2 rounded-lg glass-card border border-white/10 text-xs text-[#A1A1AA]"
            >
              {t('coll_cancel')}
            </button>
          </div>
          {!canAfford && (
            <p data-testid="collection-create-shortfall" className="text-[11px] text-[#EF4444] mt-2 text-center">
              {t('coll_need_coins')
                .replace('{cost}', fmt(createCost))
                .replace('{n}', fmt(createCost - (user?.coins || 0)))}
            </p>
          )}
        </div>
      ) : (
        <>
          <button
            data-testid="collection-create-btn"
            onClick={() => { haptic?.(); setCreating(true); }}
            disabled={!canAfford}
            className={`w-full py-3 rounded-xl flex items-center justify-center gap-2 glass-card border transition-colors active:scale-[0.98] ${
              canAfford
                ? 'border-[#D4AF37]/30 hover:border-[#D4AF37] mb-3'
                : 'border-white/10 opacity-40 cursor-not-allowed active:scale-100 mb-1.5'
            }`}
          >
            <Plus className={`w-4 h-4 ${canAfford ? 'text-[#D4AF37]' : 'text-[#71717A]'}`} />
            <span className="text-sm font-semibold text-[#F5F5F7]">
              {t('coll_new')}
            </span>
            <span className={`flex items-center gap-1 text-xs font-mono ${canAfford ? 'text-[#D4AF37]' : 'text-[#71717A]'}`}>
              <Coins className="w-3 h-3" />{fmt(createCost)}
            </span>
          </button>
          {!canAfford && (
            <p data-testid="collection-create-locked-hint" className="text-[11px] text-[#71717A] mb-3 text-center">
              {t('coll_coins_short').replace('{n}', fmt(createCost - (user?.coins || 0)))}
            </p>
          )}
        </>
      )}

      {collections.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-14 text-center" data-testid="collections-empty">
          <Layers className="w-12 h-12 text-[#52525B] mb-3" />
          <p className="text-[#A1A1AA] text-sm max-w-[260px]">
            {t('coll_empty_hint')}
          </p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {collections.map((c) => (
            <div
              key={c.id}
              data-testid={`collection-row-${c.id}`}
              onClick={() => { haptic?.(); setOpenId(c.id); }}
              className="glass-card rounded-xl p-3 border border-white/5 hover:border-[#D4AF37]/40 transition-colors cursor-pointer"
            >
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-[#F5F5F7] truncate">{c.name}</div>
                  <div className="text-[10px] text-[#A1A1AA] mt-0.5 flex items-center gap-2">
                    <span className="font-mono">{c.cards_count}/{c.slots}</span>
                    <span className="flex items-center gap-1">
                      <ChessRook className="w-3 h-3 text-[#D4AF37]" />
                      <span className="font-mono text-[#D4AF37]">{fmt(c.total_influence)}</span>
                    </span>
                    {c.best_bonus > 0 && (
                      <span data-testid={`collection-bonus-${c.id}`} className="font-mono text-[#10B981]">
                        +{c.best_bonus}%
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <Pencil className="w-3.5 h-3.5 text-[#52525B]" />
                </div>
              </div>

              {/* Slot strip: filled covers + empty/locked placeholders */}
              <SetBadges sets={c.sets} language={language} />
              <div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-0.5 mt-2">
                {c.cards.map((card) => (
                  <img
                    key={card.id}
                    src={thumb(card.image_url, 160)}
                    alt={card.title}
                    loading="lazy"
                    className="w-11 h-14 object-cover rounded-md flex-shrink-0 border border-white/10"
                    style={{ objectPosition: `${card.image_focus_x ?? 50}% ${card.image_focus_y ?? 50}%` }}
                  />
                ))}
                {Array.from({ length: Math.max(0, c.slots - c.cards_count) }).map((_, i) => (
                  <div
                    key={`e${i}`}
                    className="w-11 h-14 rounded-md flex-shrink-0 border border-dashed border-white/10 bg-white/[0.02]"
                  />
                ))}
                {c.slots < c.max_slots && (
                  <div className="w-11 h-14 rounded-md flex-shrink-0 border border-dashed border-[#D4AF37]/25 flex items-center justify-center">
                    <Lock className="w-3.5 h-3.5 text-[#D4AF37]/50" />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <CollectionManageSheet
        collection={open}
        onClose={() => setOpenId(null)}
        onChanged={() => { load(); refreshUser?.(); }}
        onDelete={handleDelete}
      />
    </div>
  );
};

export default CollectionsPanel;
